import { useState, useRef, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Eye, EyeOff } from "lucide-react";

const BASE_URL = process.env.REACT_APP_API_URL || "";

export default function ResetPasswordPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [password, setPassword] = useState("");
  const [showPw, setShowPw]     = useState(false);
  const [status, setStatus]     = useState("form"); // form | success | error
  const [error, setError]       = useState("");
  const [loading, setLoading]   = useState(false);
  const inputRef = useRef(null);

  const token = searchParams.get("token");

  useEffect(() => {
    if (!token) setStatus("invalid");
    else inputRef.current?.focus();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) { setError(t("resetPassword.tooShort")); return; }
    setLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, new_password: password }),
      });
      if (res.ok) {
        setStatus("success");
      } else {
        const data = await res.json();
        setError(data.detail || t("resetPassword.error"));
      }
    } catch {
      setError(t("resetPassword.error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: "2rem" }}>
      <div style={{ textAlign: "center", maxWidth: 400, width: "100%" }}>

        {status === "invalid" && (
          <>
            <h2>{t("resetPassword.invalidTitle")}</h2>
            <p>{t("resetPassword.invalidBody")}</p>
            <Link to="/forgot-password">{t("resetPassword.requestNew")}</Link>
          </>
        )}

        {status === "success" && (
          <>
            <h2>✓ {t("resetPassword.successTitle")}</h2>
            <p>{t("resetPassword.successBody")}</p>
            <Link to="/login">{t("forgotPassword.backToLogin")}</Link>
          </>
        )}

        {status === "form" && (
          <form className="auth-form" onSubmit={handleSubmit}>
            <h2 className="auth-title">{t("resetPassword.title")}</h2>
            {error && <p className="auth-error">{error}</p>}
            <div className="auth-pw-wrap">
              <input
                ref={inputRef}
                type={showPw ? "text" : "password"}
                placeholder={t("resetPassword.newPassword")}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
              />
              <button type="button" className="auth-pw-toggle" onClick={() => setShowPw((v) => !v)} tabIndex={-1}>
                {showPw ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
            <div className="auth-actions">
              <button type="submit" disabled={loading}>
                {loading ? "..." : t("resetPassword.submit")}
              </button>
            </div>
          </form>
        )}

      </div>
    </div>
  );
}
